import { useMemo } from "react";
import { MenuItem, Select, Stack, Switch, TextField, Typography } from "@mui/material";
import { AddRounded, CloseRounded } from "@mui/icons-material";
import ObjectIcon from "./ObjectIcon";

/**
 * Editor for the `create_record` action — the conversion step.
 *
 * Shared by the workflow builder (THEN steps) and the pipeline builder
 * (actions on a transition arrow), so the two stay identical as the action
 * gains options.
 *
 * `sourceObjectId` is the object the automation runs on; it decides which
 * relation fields on the target can link back to it.
 */
export default function CreateRecordAction({
    action,
    objects = [],
    sourceObjectId,
    onChange,
    inputSx = { fontSize: 13, background: "var(--bg-secondary)" },
    hintSx = { fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.5 },
}) {
    const target = objects.find((o) => o.id === action.objectId) || null;
    const targetFields = useMemo(() => target?.fields ?? [], [target]);

    /* Only relation fields on the TARGET that point back at the source object
       can carry the link — anything else would point somewhere unrelated. */
    const linkFields = useMemo(
        () =>
            targetFields.filter(
                (f) => f.type === "relation" && f.config?.objectId === sourceObjectId,
            ),
        [targetFields, sourceObjectId],
    );

    const mapping = action.mapping ?? {};
    const mappingRows = Object.entries(mapping);

    const setMapping = (next) => onChange({ mapping: next });

    const renameRow = (oldKey, newKey) => {
        const next = {};
        for (const [k, v] of Object.entries(mapping)) {
            if (k === oldKey) next[newKey] = v;
            else next[k] = v;
        }
        setMapping(next);
    };

    const sourceObject = objects.find((o) => o.id === sourceObjectId);
    const sampleKey = sourceObject?.fields?.[0]?.key ?? "field_key";

    return (
        <Stack sx={{ gap: 1.5 }}>
            {/* --- which object --- */}
            <Stack sx={{ gap: 0.75 }}>
                <Typography sx={hintSx}>Object to create in</Typography>
                <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={action.objectId ?? ""}
                    onChange={(e) =>
                        /* Changing the target invalidates the old field mapping
                           and link field, so clear them rather than keep keys
                           that don't exist on the new object. */
                        onChange({
                            objectId: e.target.value,
                            mapping: {},
                            linkField: undefined,
                        })
                    }
                    sx={inputSx}
                >
                    <MenuItem value="" disabled sx={{ fontSize: 13.5 }}>
                        Pick an object
                    </MenuItem>
                    {objects.map((o) => (
                        <MenuItem key={o.id} value={o.id} sx={{ fontSize: 13.5 }}>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <ObjectIcon name={o.icon} size={15} />
                                {o.name}
                                {o.id === sourceObjectId && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 12,
                                            color: "var(--text-tertiary)",
                                        }}
                                    >
                                        (this object)
                                    </Typography>
                                )}
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
            </Stack>

            {!target && (
                <Typography sx={hintSx}>
                    Pick the object the new record should be created in — for
                    example a Contact created from a won Lead.
                </Typography>
            )}

            {target && (
                <>
                    {/* --- link back --- */}
                    <Stack sx={{ gap: 0.75 }}>
                        <Typography sx={hintSx}>Link back via</Typography>
                        <Select
                            size="small"
                            fullWidth
                            displayEmpty
                            value={action.linkField ?? ""}
                            onChange={(e) => {
                                const linkField = e.target.value || undefined;
                                onChange({
                                    linkField,
                                    /* Duplicate protection only means anything
                                       when there is a link to check. */
                                    skipIfLinked: linkField
                                        ? action.skipIfLinked !== false
                                        : false,
                                });
                            }}
                            sx={inputSx}
                            disabled={!linkFields.length}
                        >
                            <MenuItem value="" sx={{ fontSize: 13.5 }}>
                                — none —
                            </MenuItem>
                            {linkFields.map((f) => (
                                <MenuItem
                                    key={f.key}
                                    value={f.key}
                                    sx={{ fontSize: 13.5 }}
                                >
                                    {f.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography sx={hintSx}>
                            {linkFields.length
                                ? "Points the new record back at this one, so they show up in each other's linked records."
                                : `${target.name} has no relation field pointing at this object yet — add one in Objects to link them.`}
                        </Typography>
                    </Stack>

                    {action.linkField && (
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                Don&apos;t create a duplicate if already linked
                            </Typography>
                            <Switch
                                size="small"
                                checked={action.skipIfLinked !== false}
                                onChange={(e) =>
                                    onChange({ skipIfLinked: e.target.checked })
                                }
                            />
                        </Stack>
                    )}

                    {/* --- field mapping --- */}
                    <Stack sx={{ gap: 1 }}>
                        <Typography sx={hintSx}>Field mapping</Typography>

                        {mappingRows.map(([key, value]) => (
                            <Stack
                                key={key}
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 0.75,
                                }}
                            >
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={key}
                                    onChange={(e) =>
                                        renameRow(key, e.target.value)
                                    }
                                    sx={{ ...inputSx, flex: 1, minWidth: 0 }}
                                >
                                    {targetFields.map((f) => (
                                        <MenuItem
                                            key={f.key}
                                            value={f.key}
                                            disabled={
                                                f.key !== key && f.key in mapping
                                            }
                                            sx={{ fontSize: 13.5 }}
                                        >
                                            {f.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <TextField
                                    size="small"
                                    placeholder={`{{${sampleKey}}}`}
                                    value={value ?? ""}
                                    onChange={(e) =>
                                        setMapping({
                                            ...mapping,
                                            [key]: e.target.value,
                                        })
                                    }
                                    InputProps={{ sx: inputSx }}
                                    sx={{ flex: 1.2, minWidth: 0 }}
                                />
                                <CloseRounded
                                    onClick={() => {
                                        const next = { ...mapping };
                                        delete next[key];
                                        setMapping(next);
                                    }}
                                    sx={{
                                        fontSize: 16,
                                        cursor: "pointer",
                                        color: "var(--text-tertiary)",
                                    }}
                                />
                            </Stack>
                        ))}

                        {(() => {
                            const unmapped = targetFields.filter(
                                (f) => !(f.key in mapping),
                            );
                            if (!unmapped.length) return null;
                            return (
                                <Stack
                                    onClick={() =>
                                        setMapping({
                                            ...mapping,
                                            [unmapped[0].key]: "",
                                        })
                                    }
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 0.75,
                                        cursor: "pointer",
                                        color: "var(--accent)",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        width: "fit-content",
                                    }}
                                >
                                    <AddRounded sx={{ fontSize: 15 }} />
                                    Map a field
                                </Stack>
                            );
                        })()}

                        <Typography sx={hintSx}>
                            {mappingRows.length
                                ? `Use ${"{{"}${sampleKey}${"}}"} to copy a value from this record.`
                                : "No mapping set — fields the two objects share are copied automatically."}
                        </Typography>
                    </Stack>
                </>
            )}
        </Stack>
    );
}
